/**
 * queryClient.js
 *
 * Shared TanStack React Query client instance.
 * Configure caching, retry, stale time, etc. once here — applies app-wide.
 *
 * Usage:
 *   import { queryClient } from 'services/queryClient';
 *   // Then wrap App with <QueryClientProvider client={queryClient}>
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 2 minutes — prevents redundant refetches
      // when components re-mount or user navigates back to a page
      staleTime: 2 * 60 * 1000, // 2 minutes

      // Keep inactive/unmounted query data in cache for 10 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime in v4)

      // Retry failed requests once before showing error
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),

      // Refetch when user switches back to the tab (keeps data fresh for SaaS)
      refetchOnWindowFocus: true,

      // Don't refetch on reconnect by default (avoid flooding after connectivity issues)
      refetchOnReconnect: true,

      // Don't refetch on component mount if data is still fresh
      refetchOnMount: true,
    },

    mutations: {
      // Never retry mutations (side effects should not be repeated automatically)
      retry: 0,
    },
  },
});
